import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import config from '../../../config/config';

export function CustomerRegistrationForm() {
  const { customerId } = useParams();
  const navigate = useNavigate();

  const [customerDetails, setCustomerDetails] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchFormData = async () => {
      try {
        const token = localStorage.getItem('token');
        const response = await fetch(`${config.baseUrl}/api/moderator/customer-full-details/${customerId}`, {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        });
        if (!response.ok) throw new Error('Failed to fetch registration details');
        const data = await response.json();
        setCustomerDetails(data);
        setLoading(false);
      } catch (err) {
        setError(err.message);
        setLoading(false);
      }
    };
    if (customerId) fetchFormData();
  }, [customerId]);

  const handleformPrint = () => window.print();

  const handlePrint = async (id, shareId) => {
    try {
      const response = await fetch(`${config.baseUrl}/api/moderator/generate-slip/${id}/${shareId}`);
      if (!response.ok) throw new Error('Failed to fetch payment data');
      const paymentData = await response.json();
      localStorage.setItem('temp_slip_data', JSON.stringify(paymentData));
      window.open('/slip', '_blank');
    } catch (error) {
      console.error('Error printing slip:', error);
    }
  };

  if (loading) return (
    <div className="flex items-center justify-center min-h-screen bg-slate-50">
      <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-blue-600"></div>
    </div>
  );

  if (error || !customerDetails) return (
    <div className="p-8 text-center text-red-600 bg-slate-50 min-h-screen">
      <div className="max-w-md mx-auto bg-white p-6 rounded-xl shadow-lg">
        <p className="font-bold text-lg">Oops! Something went wrong</p>
        <p className="text-sm text-gray-500 mb-4">{error || "Customer data not found"}</p>
        <button onClick={() => navigate(-1)} className="bg-blue-600 text-white px-6 py-2 rounded-lg font-bold">Go Back</button>
      </div>
    </div>
  );

  return (
    <>
      {/* Header */}
      <div className="flex flex-col md:flex-row justify-between items-center border-b-4 border-indigo-800 pb-8 mb-10 gap-4">
        <div className="text-center md:text-left">
          <h1 className="text-4xl font-black text-blue-900 uppercase tracking-tighter">Registration Form</h1>
        </div>
        <div className="bg-indigo-800 text-white px-6 py-3 rounded-2xl text-center shadow-lg shadow-indigo-200">
          <p className="text-[10px] uppercase font-black opacity-80 leading-none mb-1">Customer ID</p>
          <p className="text-2xl font-mono font-black">{customerId}</p>
        </div>
      </div>

      {/* Basic Info Section */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-12">
        <div className="space-y-1">
          <label className="text-[11px] font-black text-slate-400 uppercase ml-1">Customer Name</label>
          <div className="p-4 bg-slate-50 border border-slate-100 rounded-2xl font-bold text-slate-700 shadow-sm leading-none capitalize">
            {customerDetails.customer_name}
          </div>
        </div>
        <div className="space-y-1">
          <label className="text-[11px] font-black text-slate-400 uppercase ml-1">Phone Number</label>
          <div className="p-4 bg-slate-50 border border-slate-100 rounded-2xl font-bold text-slate-700 shadow-sm leading-none italic">
            {customerDetails.phone}
          </div>
        </div>
        <div className="space-y-1">
          <label className="text-[11px] font-black text-slate-400 uppercase ml-1">Payment Status</label>
          <div className={`p-4 rounded-2xl font-black text-center shadow-sm uppercase text-sm leading-none border ${customerDetails.payment_status === 'paid'
            ? 'bg-blue-100 text-blue-700 border-blue-200'
            : 'bg-amber-100 text-amber-700 border-amber-200'
            }`}>
            {customerDetails.payment_status}
          </div>
        </div>
        <div className="md:col-span-3 space-y-1">
          <label className="text-[11px] font-black text-slate-400 uppercase ml-1">Full Delivery Address</label>
          <div className="p-4 bg-slate-50 border border-slate-100 rounded-2xl font-semibold text-slate-600 shadow-sm">
            {customerDetails.address || "No address provided"}
          </div>
        </div>
      </div>

      {/* Shares Table */}
      <div className="mb-12">
        <div className="flex items-center gap-3 mb-6">
          <div className="h-8 w-2 bg-indigo-800 rounded-full"></div>
          <h3 className="text-2xl font-black text-slate-800 tracking-tight">Registered Shares Summary</h3>
        </div>

        <div className="overflow-hidden border border-indigo-200 rounded-3xl shadow-sm">
          <table className="w-full text-left">
            <thead className="bg-indigo-800 text-white">
              <tr className="text-[11px] font-black uppercase tracking-widest">
                <th className="px-4 py-5 text-center">#</th>
                <th className="px-6 py-5">Person Details</th>
                <th className="px-6 py-5">Animal Info</th>
                <th className="px-4 py-5 text-center">Day</th>
                <th className="px-4 py-5 text-center">Animal #</th>
                <th className="px-4 py-5 text-center">Share #</th>
                <th className="px-6 py-5 text-right print:hidden">Action</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {customerDetails.shares_data && customerDetails.shares_data.length > 0 ? (
                customerDetails.shares_data.map((share, idx) => (
                  <tr key={idx} className="hover:bg-indigo-50/50 transition-colors group">
                    <td className="px-4 py-4 text-center font-mono text-slate-400 text-xs">{idx + 1}</td>
                    <td className="px-6 py-4">
                      <p className="font-bold text-slate-800 leading-tight capitalize">
                        {`${share.firstname || ''} ${share.lastname || ''}`.trim()}
                      </p>
                      <p className="text-[10px] text-slate-400 uppercase font-bold tracking-tighter mt-1">Registered Share</p>
                    </td>
                    <td className="px-6 py-4">
                      <div className="flex flex-col">
                        <span className="text-sm font-bold text-blue-900">{share.animal_type}</span>
                        <span className="text-[10px] font-bold text-slate-400 uppercase">{share.category}</span>
                      </div>
                    </td>
                    <td className="px-4 py-4 text-center">
                      <span className="bg-slate-100 text-slate-600 px-3 py-1 rounded-full text-xs font-black">
                        DAY {share.slaughtering_day}
                      </span>
                    </td>
                    <td className="px-4 py-4 text-center">
                      <div className="w-10 h-10 rounded-xl bg-blue-50 border-2 border-blue-100 flex items-center justify-center mx-auto text-blue-700 font-black">
                        {share.animal_number}
                      </div>
                    </td>
                    <td className="px-4 py-4 text-center">
                      <div className="w-10 h-10 rounded-xl bg-indigo-50 border-2 border-indigo-100 flex items-center justify-center mx-auto text-indigo-700 font-black">
                        {share.share_number}
                      </div>
                    </td>
                    <td className="px-6 py-4 text-right print:hidden">
                      <button
                        onClick={() => handlePrint(customerDetails.id, share.share_id)}
                        className="bg-indigo-800 text-white px-4 py-1.5 rounded-xl hover:bg-indigo-700 hover:text-white transition-all font-black text-[10px] uppercase shadow-sm active:scale-95"
                      >
                        Print Slip
                      </button>
                    </td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td colSpan="7" className="p-16 text-center">
                    <p className="text-slate-400 font-bold italic">No shares data available for this ID.</p>
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Footer for Printing */}
      <div className="hidden print:grid grid-cols-2 gap-20 mt-24 px-10">
        <div className="text-center border-t-2 border-slate-900 pt-4">
          <p className="text-xs font-black uppercase tracking-widest">Customer Signature</p>
        </div>
        <div className="text-center border-t-2 border-slate-900 pt-4">
          <p className="text-xs font-black uppercase tracking-widest">Authorized Stamp</p>
        </div>
      </div>

      {/* Action Bar */}
      <div className="flex flex-col sm:flex-row justify-center gap-4 print:hidden pt-10 border-t border-slate-100 mt-10">
        <button
          onClick={() => navigate(-1)}
          className="bg-slate-800 text-white px-10 py-4 rounded-2xl font-black hover:bg-slate-900 transition-all active:scale-95 uppercase text-sm tracking-widest"
        >
          Go Back
        </button>
      </div>
    </>
  );
}